import { useToast } from '@chakra-ui/react';
import {
  useExternalGetDarwinPropertyClassificationLazyQuery,
  useExternalGetDarwinSyncPreviewLazyQuery,
  useExternalSyncDarwinVendorsMutation,
} from '@client/graphql/__generated__/document-operations';
import {
  ExternalDarwinPropertiesDocument,
  useExternalDarwinPropertiesQuery,
  useExternalDarwinPropertyLazyQuery,
  useExternalSyncKeyTermsMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  DarwinPropertyDetailField,
  DarwinPropertyDetailFieldLabel,
  DarwinSyncPersonField,
  DarwinSyncPreviewSectionType,
  DarwinVendorType,
  ExternalDarwinPropertiesQuery,
  ExternalDarwinPropertyQuery,
  ExternalGetDarwinSyncPreviewQuery,
  ExternalIngestionFolderFragment,
  ExternalSyncKeyTermsInput,
  ExternalSystem,
  ParticipantRole,
} from '@client/graphql/__generated__/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FeatureKey,
  useIsFeatureEnabledForPayload,
} from '~/apps/consumer/hooks/useIsFeatureEnabledForPayload';
import { MUTE_ERROR_TOAST_QUERY_OPTIONS } from '~/services/document/utils/apollo-client';

/**
 * Keys for Darwin property detail fields that can be retrieved.
 */
export type DarwinPropertyDetailKey =
  | 'typeCodeName'
  | 'propertyTypeName'
  | 'classificationName';

/**
 * Values for Darwin property details, where each field may be a string or null.
 */
export type DarwinPropertyDetailValues = Partial<
  Record<DarwinPropertyDetailKey, string | null>
>;

/**
 * Selection of Darwin property detail fields to sync.
 */
export type DarwinPropertyDetailSelection = DarwinPropertyDetailField[];

export interface PropertyOption {
  value: string;
  label: string;
}

/**
 * Status sync preview field showing current vs proposed values.
 */
export type DarwinStatusSyncPreview =
  ExternalGetDarwinSyncPreviewQuery['externalGetDarwinSyncPreview']['sections'][number]['fields'][number];

/**
 * Vendors sync preview section showing lender and title company fields.
 */
export type DarwinVendorsSyncPreview =
  ExternalGetDarwinSyncPreviewQuery['externalGetDarwinSyncPreview']['vendorsSection'];

interface UseDarwinSyncOptions {
  folder?: ExternalIngestionFolderFragment | null;
  selectedOrganizationId?: string;
  variant: 'external' | 'internal';
}

/** User's vendor selection overrides (keyed by vendor type) */
export type VendorSelections = Partial<Record<DarwinVendorType, number>>;

interface UseDarwinSyncReturn {
  isDarwinSyncEnabled: boolean;
  darwinProperties: DarwinPropertyWithParticipants[] | undefined;
  darwinPropertyLoading: boolean;
  darwinSyncLoading: boolean;
  selectedProperty: PropertyOption | null;
  setSelectedProperty: (property: PropertyOption | null) => void;
  propertyOptions: PropertyOption[];
  propertyClassification: DarwinPropertyDetailValues | null;
  propertyClassificationLoading: boolean;
  statusSyncPreview: DarwinStatusSyncPreview | null;
  statusSyncPreviewLoading: boolean;
  vendorsSyncPreview: DarwinVendorsSyncPreview | null;
  vendorsSyncPreviewLoading: boolean;
  vendorSelections: VendorSelections;
  setVendorSelection: (vendorType: DarwinVendorType, personId: number) => void;
  handleDarwinSync: (
    selectedFields?: string[],
    selectedParticipants?: ParticipantRole[],
    selectedPropertyId?: string | null,
    selectedPropertyDetails?: DarwinPropertyDetailSelection,
    syncStatus?: boolean
  ) => Promise<string | null>;
  handleVendorSync: (
    selectedVendorFields: string[],
    selectedPropertyId: string
  ) => Promise<void>;
  vendorSyncLoading: boolean;
}

type BaseDarwinProperty =
  ExternalDarwinPropertiesQuery['externalDarwinProperties']['properties'][number];

type DarwinPropertyParticipants =
  ExternalDarwinPropertyQuery['externalDarwinProperties']['properties'][number]['participants'];

export type DarwinPropertyWithParticipants = BaseDarwinProperty & {
  participants?: DarwinPropertyParticipants;
};

export const useDarwinSync = ({
  folder,
  selectedOrganizationId,
  variant,
}: UseDarwinSyncOptions): UseDarwinSyncReturn => {
  const toast = useToast();
  const [selectedProperty, setSelectedProperty] =
    useState<PropertyOption | null>(null);

  const externalFolderId = folder?.folderId;

  // For internal variant, use the folder's organization instead of the selected organization
  const organizationIdForFeatureFlag =
    variant === 'internal' ? folder?.organizationUuid : selectedOrganizationId;

  const isSyncKeyTermsToDarwinFlagEnabled = useIsFeatureEnabledForPayload({
    featureFlagKey: FeatureKey.SYNC_KEY_TERMS_TO_DARWIN,
    organizationId: organizationIdForFeatureFlag,
  });

  const isDarwinSyncEnabled = Boolean(
    isSyncKeyTermsToDarwinFlagEnabled &&
      folder?.organizationUuid &&
      (variant === 'internal' ||
        (selectedOrganizationId &&
          folder?.organizationUuid === selectedOrganizationId)) &&
      // TODO: BLO-2556 - support folders that we can't find a MLSListingObject for
      folder?.mlsListing?.listingId
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const darwinSyncUnavailableReason = useMemo(() => {
    if (!isSyncKeyTermsToDarwinFlagEnabled) {
      return null;
    }

    if (!folder) {
      return null;
    }

    if (variant === 'external') {
      if (!selectedOrganizationId) {
        return null;
      }

      if (folder.organizationUuid !== selectedOrganizationId) {
        return 'The selected organization does not match this transaction.';
      }
    }

    if (!folder.mlsListing?.listingId) {
      return 'Could not find an MLS listing for this transaction.';
    }

    return null;
  }, [
    folder,
    isSyncKeyTermsToDarwinFlagEnabled,
    selectedOrganizationId,
    variant,
  ]);

  const { data: allDarwinPropertyData, loading: allDarwinPropertyLoading } =
    useExternalDarwinPropertiesQuery({
      variables: {
        input: {
          externalFolderId,
        },
      },
      skip: !isDarwinSyncEnabled || !externalFolderId,
      ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
    });

  const [
    fetchSelectedDarwinProperty,
    {
      data: selectedDarwinPropertyData,
      loading: selectedDarwinPropertyLoading,
    },
  ] = useExternalDarwinPropertyLazyQuery({
    fetchPolicy: 'network-only',
    ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
  });

  const [
    fetchPropertyClassification,
    {
      data: propertyClassificationData,
      loading: propertyClassificationLoading,
    },
  ] = useExternalGetDarwinPropertyClassificationLazyQuery({
    fetchPolicy: 'network-only',
    ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
  });

  const [
    fetchSyncPreview,
    { data: syncPreviewData, loading: syncPreviewLoading },
  ] = useExternalGetDarwinSyncPreviewLazyQuery({
    fetchPolicy: 'network-only',
    ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
  });

  const [syncKeyTerms, { loading: syncKeyTermsLoading }] =
    useExternalSyncKeyTermsMutation();

  const [syncDarwinVendors, { loading: vendorSyncLoading }] =
    useExternalSyncDarwinVendorsMutation();

  // Track user's vendor selection overrides (defaults to best match from preview)
  const [vendorSelections, setVendorSelections] = useState<VendorSelections>(
    {}
  );

  const setVendorSelection = useCallback(
    (vendorType: DarwinVendorType, personId: number) => {
      setVendorSelections((prev) => ({
        ...prev,
        [vendorType]: personId,
      }));
    },
    []
  );

  useEffect(() => {
    if (!selectedProperty?.value || !externalFolderId) {
      return;
    }

    const propertyId = parseInt(selectedProperty.value, 10);

    if (Number.isNaN(propertyId)) {
      return;
    }

    void fetchSelectedDarwinProperty({
      variables: {
        input: {
          externalFolderId,
          propertyId,
        },
      },
    });
  }, [externalFolderId, fetchSelectedDarwinProperty, selectedProperty?.value]);

  // Fetch property classification for selected property
  useEffect(() => {
    if (!selectedProperty?.value || !externalFolderId || !folder?.id) {
      return;
    }

    const propertyId = parseInt(selectedProperty.value, 10);

    if (Number.isNaN(propertyId)) {
      return;
    }

    void fetchPropertyClassification({
      variables: {
        input: {
          ingestionFolderId: folder.id,
          propertyId,
        },
      },
    });
  }, [
    externalFolderId,
    folder?.id,
    fetchPropertyClassification,
    selectedProperty?.value,
  ]);

  // Fetch sync preview for selected property (or without propertyId for new property creation)
  useEffect(() => {
    if (!externalFolderId || !folder?.id) {
      return;
    }

    const propertyId = selectedProperty?.value
      ? parseInt(selectedProperty.value, 10)
      : undefined;

    if (selectedProperty?.value && Number.isNaN(propertyId)) {
      return;
    }

    void fetchSyncPreview({
      variables: {
        input: {
          ingestionFolderId: folder.id,
          propertyId,
        },
      },
    });
  }, [externalFolderId, folder?.id, fetchSyncPreview, selectedProperty?.value]);

  const baseDarwinProperties = useMemo<
    DarwinPropertyWithParticipants[] | undefined
  >(() => {
    const properties =
      allDarwinPropertyData?.externalDarwinProperties?.properties;
    if (properties === undefined) {
      return undefined;
    }

    return properties;
  }, [allDarwinPropertyData?.externalDarwinProperties?.properties]);

  const selectedDarwinPropertyRecord = useMemo<
    DarwinPropertyWithParticipants | undefined
  >(() => {
    if (!selectedProperty?.value) {
      return undefined;
    }

    const property =
      selectedDarwinPropertyData?.externalDarwinProperties?.properties?.[0];

    if (!property) {
      return undefined;
    }

    const selectedPropertyId = parseInt(selectedProperty.value, 10);

    if (Number.isNaN(selectedPropertyId)) {
      return undefined;
    }

    if (property.propertyId !== selectedPropertyId) {
      return undefined;
    }

    return property;
  }, [
    selectedDarwinPropertyData?.externalDarwinProperties?.properties,
    selectedProperty?.value,
  ]);

  const darwinProperties = useMemo<
    DarwinPropertyWithParticipants[] | undefined
  >(() => {
    if (!baseDarwinProperties) {
      return undefined;
    }

    if (!selectedDarwinPropertyRecord) {
      return baseDarwinProperties;
    }

    const propertyExists = baseDarwinProperties.some(
      (property) =>
        property.propertyId === selectedDarwinPropertyRecord.propertyId
    );

    if (!propertyExists) {
      return [...baseDarwinProperties, selectedDarwinPropertyRecord];
    }

    return baseDarwinProperties.map((property) =>
      property.propertyId === selectedDarwinPropertyRecord.propertyId
        ? selectedDarwinPropertyRecord
        : property
    );
  }, [baseDarwinProperties, selectedDarwinPropertyRecord]);

  // Property options for selector
  const propertyOptions: PropertyOption[] = useMemo(() => {
    const options: PropertyOption[] = [];

    // Add all matching Darwin properties
    darwinProperties
      ?.slice()
      .sort((a, b) => b.propertyId - a.propertyId)
      .forEach((property) => {
        options.push({
          value: property.propertyId.toString(),
          label: property.propertyAddress
            ? `${property.propertyAddress} (PID: ${property.propertyId})`
            : `PID: ${property.propertyId}`,
        });
      });

    return options;
  }, [darwinProperties]);

  // Compute property classification from query result
  const propertyClassification =
    useMemo<DarwinPropertyDetailValues | null>(() => {
      const classification =
        propertyClassificationData?.externalGetDarwinPropertyClassification;

      if (!classification) {
        return null;
      }

      return {
        typeCodeName: classification.typeCodeName ?? null,
        propertyTypeName: classification.propertyTypeName ?? null,
        classificationName: classification.classificationName ?? null,
      };
    }, [propertyClassificationData]);

  // Extract status field from PROPERTY_DETAILS section
  const statusSyncPreview = useMemo<DarwinStatusSyncPreview | null>(() => {
    const sections = syncPreviewData?.externalGetDarwinSyncPreview?.sections;
    if (!sections) {
      return null;
    }

    const propertyDetailsSection = sections.find(
      (section) =>
        section.sectionType === DarwinSyncPreviewSectionType.PROPERTY_DETAILS
    );

    if (!propertyDetailsSection) {
      return null;
    }

    // Find the Status field within the section
    return (
      propertyDetailsSection.fields.find(
        (field) => field.label === DarwinPropertyDetailFieldLabel.STATUS
      ) ?? null
    );
  }, [syncPreviewData]);

  // Extract vendors section (lender and title company)
  const vendorsSyncPreview = useMemo<DarwinVendorsSyncPreview | null>(() => {
    return (
      syncPreviewData?.externalGetDarwinSyncPreview?.vendorsSection ?? null
    );
  }, [syncPreviewData]);

  const darwinPropertyLoading =
    allDarwinPropertyLoading || selectedDarwinPropertyLoading;

  // Set default selected property when properties first load
  useEffect(() => {
    if (
      propertyOptions.length > 0 &&
      !selectedProperty &&
      !darwinPropertyLoading
    ) {
      setSelectedProperty(propertyOptions[0]);
    }
  }, [propertyOptions, selectedProperty, darwinPropertyLoading]);

  const handleDarwinSync = useCallback(
    async (
      selectedFields?: string[],
      selectedParticipants?: ParticipantRole[],
      selectedPropertyId?: string,
      selectedPropertyDetails?: DarwinPropertyDetailSelection | null,
      syncStatus?: boolean
    ) => {
      // Handle property details selection:
      // - undefined: not specified, don't include in sync input
      // - null: explicitly clear selection, send empty array
      // - array: sync the specified fields
      let propertyDetailsSelection: DarwinPropertyDetailSelection | undefined;

      if (selectedPropertyDetails === null) {
        propertyDetailsSelection = [];
      } else if (Array.isArray(selectedPropertyDetails)) {
        propertyDetailsSelection = selectedPropertyDetails.filter(Boolean);
      }

      const hasSelectedPropertyDetails =
        (propertyDetailsSelection?.length ?? 0) > 0;
      const hasAnySelections =
        Boolean(selectedFields?.length) ||
        Boolean(selectedParticipants?.length) ||
        hasSelectedPropertyDetails ||
        typeof syncStatus === 'boolean';

      if (!externalFolderId || !hasAnySelections) {
        return null;
      }

      try {
        const input: ExternalSyncKeyTermsInput = {
          externalFolderId,
          toSystem: ExternalSystem.DARWIN,
          externalSystemIdentifier: selectedPropertyId,
          onlyKeyTerms: {
            keyTerms: (selectedFields || []).map((term) => ({ term })),
          },
        };

        if (selectedParticipants?.length) {
          input.onlyParticipants = selectedParticipants.map((role) => ({
            role,
          }));
        }

        // Include environment options for property details and status sync
        if (
          propertyDetailsSelection !== undefined ||
          syncStatus !== undefined
        ) {
          input.environment = {
            darwin: {
              ...(propertyDetailsSelection !== undefined
                ? { propertyDetailsToSync: propertyDetailsSelection }
                : {}),
              ...(syncStatus !== undefined ? { syncStatus } : {}),
            },
          };
        }

        const response = await syncKeyTerms({
          variables: {
            input,
          },
          refetchQueries: [
            {
              query: ExternalDarwinPropertiesDocument,
              variables: {
                input: {
                  externalFolderId,
                },
              },
            },
          ],
          awaitRefetchQueries: true,
          ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
        });

        // Get the external entity ID from the response (e.g., newly created Darwin property ID)
        const externalEntityId =
          response.data?.externalSyncKeyTerms?.externalEntityId ?? null;

        const propertyIdToRefresh =
          externalEntityId ??
          selectedPropertyId ??
          selectedProperty?.value ??
          null;

        if (propertyIdToRefresh) {
          const parsedPropertyId = parseInt(propertyIdToRefresh, 10);

          if (!Number.isNaN(parsedPropertyId)) {
            await fetchSelectedDarwinProperty({
              variables: {
                input: {
                  externalFolderId,
                  propertyId: parsedPropertyId,
                },
              },
            });

            // Refetch sync preview and property classification to reflect updated state
            if (folder?.id) {
              void fetchSyncPreview({
                variables: {
                  input: {
                    ingestionFolderId: folder.id,
                    propertyId: parsedPropertyId,
                  },
                },
              });

              void fetchPropertyClassification({
                variables: {
                  input: {
                    ingestionFolderId: folder.id,
                    propertyId: parsedPropertyId,
                  },
                },
              });
            }
          }
        } else if (folder?.id) {
          // Creating new property - refetch sync preview without propertyId
          void fetchSyncPreview({
            variables: {
              input: {
                ingestionFolderId: folder.id,
              },
            },
          });
        }

        toast({
          title: 'Synced to Darwin',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        return externalEntityId;
      } catch (error) {
        const description =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred.';

        toast({
          title: 'Failed to sync to Darwin',
          status: 'error',
          description,
          duration: 5000,
          isClosable: true,
        });

        return null;
      }
    },
    [
      externalFolderId,
      fetchPropertyClassification,
      fetchSelectedDarwinProperty,
      fetchSyncPreview,
      folder?.id,
      selectedProperty?.value,
      syncKeyTerms,
      toast,
    ]
  );

  /**
   * Syncs selected vendors to Darwin using the new vendor-specific mutation.
   * Uses pre-computed preview data and user selections to avoid re-computation.
   */
  const handleVendorSync = useCallback(
    async (selectedVendorFields: string[], selectedPropertyId: string) => {
      if (!folder?.id || !vendorsSyncPreview?.fields) {
        return;
      }

      const propertyId = parseInt(selectedPropertyId, 10);
      if (Number.isNaN(propertyId)) {
        return;
      }

      try {
        // Build vendor sync input from selected fields and user selections
        const vendors = vendorsSyncPreview.fields
          .filter((field) => selectedVendorFields.includes(field.label))
          .map((field) => {
            const { vendorType } = field;
            if (!vendorType) {
              throw new Error(`Missing vendorType for field: ${field.label}`);
            }

            // Use user's selection if available, otherwise use best match from preview
            const selectedPersonId =
              vendorSelections[vendorType] ??
              field.proposedValue.matchedPersonId;

            if (!selectedPersonId) {
              throw new Error(
                `No person selected for ${field.label} and no best match available`
              );
            }

            // Include full preview data for audit trail
            return {
              vendorType,
              selectedPersonId,
              previewData: field as DarwinSyncPersonField,
            };
          });

        if (vendors.length === 0) {
          return;
        }

        await syncDarwinVendors({
          variables: {
            input: {
              ingestionFolderId: folder.id,
              darwinPropertyId: propertyId,
              vendors,
              previewTimestamp: new Date(),
            },
          },
          ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
        });

        // Refetch sync preview to reflect updated state
        if (folder.id) {
          void fetchSyncPreview({
            variables: {
              input: {
                ingestionFolderId: folder.id,
                propertyId,
              },
            },
          });
        }

        toast({
          title: 'Vendors synced to Darwin',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (error) {
        const description =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred.';

        toast({
          title: 'Failed to sync vendors to Darwin',
          status: 'error',
          description,
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [
      fetchSyncPreview,
      folder?.id,
      syncDarwinVendors,
      toast,
      vendorSelections,
      vendorsSyncPreview?.fields,
    ]
  );

  if (!isDarwinSyncEnabled) {
    return {
      isDarwinSyncEnabled: false,
      darwinProperties: [],
      darwinPropertyLoading: false,
      darwinSyncLoading: false,
      selectedProperty: null,
      setSelectedProperty: () => {},
      propertyOptions: [],
      propertyClassification: null,
      propertyClassificationLoading: false,
      statusSyncPreview: null,
      statusSyncPreviewLoading: false,
      vendorsSyncPreview: null,
      vendorsSyncPreviewLoading: false,
      vendorSelections: {},
      setVendorSelection: () => {},
      handleDarwinSync: () => Promise.resolve(null),
      handleVendorSync: () => Promise.resolve(),
      vendorSyncLoading: false,
    };
  }

  return {
    isDarwinSyncEnabled,
    darwinProperties,
    darwinPropertyLoading,
    darwinSyncLoading: syncKeyTermsLoading,
    selectedProperty,
    setSelectedProperty,
    propertyOptions,
    propertyClassification,
    propertyClassificationLoading,
    statusSyncPreview,
    statusSyncPreviewLoading: syncPreviewLoading,
    vendorsSyncPreview,
    vendorsSyncPreviewLoading: syncPreviewLoading,
    vendorSelections,
    setVendorSelection,
    handleDarwinSync,
    handleVendorSync,
    vendorSyncLoading,
  };
};
